import React, { Component } from 'react';
import { func, bool, string, shape } from 'prop-types';
import { validate as validator } from 'ui-validator';
import { Page } from '../../components';
import validations from './address-validations';
import { heapTrackErrors, heapTrackEvent } from '@shared_modules/baui-heap-tracking';
import getHeaderTitle from './header-title.js';
import { AutocompleteAddress, Checkbox } from '@shared_modules/local-propulsion/components';
import { initAddressProps, isPOBox, isPO, PO_WARNING_TEXT } from '@shared_modules/baui-utils/client/utils/address';

class Address extends Component {
  state = {
    address: initAddressProps(this.props),
    secondaryUsePrimaryAddress: this.props.address.secondaryUsePrimaryAddress,
    showHelperText: {
      state:
        ['GA', 'ND'].includes(this.props.address.state) ||
        ['GA', 'ND'].includes(this.props.sharedAddress.state) ||
        false,
      streetAddress: isPO(this.props),
    },
    validationErrors: {},
    warningMessages: isPO(this.props) ? { streetAddress: PO_WARNING_TEXT } : {},
    hasSeenAndFixedPOWarning: false,
  };

  clearValidationErrors = () => {
    if (Object.keys(this.state.validationErrors).length > 0) {
      this.setState({
        validationErrors: {},
      });
    }
  };

  validateInput = fieldName => {
    const model = this.state.address;
    const { [fieldName]: omit, ...restOfValidationErrors } = this.state.validationErrors;
    const validationErrors = validator(model, validations(fieldName));
    if (fieldName) {
      return {
        ...restOfValidationErrors,
        ...validationErrors,
      };
    }

    return validationErrors;
  };

  handleInputChange = event => {
    const { target } = event;
    const { name } = target;

    const value = name === 'zip' ? target.value.replace(/\D/g, '') : target.value;
    this.setState({
      address: {
        ...this.state.address,
        // format state to upperCase
        [name]: name === 'state' ? value.toUpperCase() : value,
      },
    });
  };

  handleBlur = event => {
    const name = event.target.name;
    let warningMessages = { ...this.state.warningMessages };
    // setTimeout is needed to remove helper text AFTER other events fired (e.g click on checkbox)
    setTimeout(() => {
      const validationErrors = this.validateInput(name);

      if (!validationErrors.length && name === 'streetAddress') {
        if (isPOBox(this.state.address.streetAddress)) {
          // user enters a potential P.O Box and there isn't already a warning showing
          if (!this.state.warningMessages.streetAddress) {
            heapTrackEvent('PL PI1 Address Page entered potential P.O. Box', {
              eventValue: 'showing P.O. warning text',
            });
          }
          warningMessages = {
            ...warningMessages,
            streetAddress: PO_WARNING_TEXT,
          };
        } else {
          // user fixes P.O warning (enters a non P.O Box street address after there is already a warning)
          if (this.state.warningMessages.streetAddress) {
            heapTrackEvent('PL PI1 Address Page fixed potential P.O Box', { eventValue: 'removing P.O. warning text' });
            this.setState({ hasSeenAndFixedPOWarning: true });
          }
          delete warningMessages.streetAddress;
        }
      }

      this.setState({
        showHelperText: {
          ...this.state.showHelperText,
          [name]: name === 'streetAddress' && warningMessages.streetAddress && warningMessages.streetAddress.length > 0,
        },
        validationErrors,
        warningMessages,
      });
    }, 500);
  };

  handleFocus = event => {
    const name = event.target.name;
    const hasError = !!this.state.validationErrors[name];
    this.setState({
      showHelperText: {
        ...this.state.showHelperText,
        [name]: !hasError,
      },
    });
  };

  handleCheckboxChange = event => {
    const { sharedAddress, address, addNewCoBorrower, isRMEJA } = this.props;
    const checked = event.target.checked;
    const hasWarning = this.state.warningMessages.streetAddress && this.state.warningMessages.streetAddress.length > 0;
    let streetAddress = '';
    let city = '';
    let state = '';
    let zip = '';
    if (isRMEJA && !checked && !addNewCoBorrower) {
      streetAddress = address.streetAddress;
      city = address.city;
      state = address.state;
      zip = address.zip;
    }
    this.setState({
      address: {
        streetAddress: checked ? sharedAddress.streetAddress : streetAddress,
        city: checked ? sharedAddress.city : city,
        state: checked ? sharedAddress.state : state,
        zip: checked ? sharedAddress.zip : zip,
      },
      secondaryUsePrimaryAddress: checked,
      showHelperText: {
        ...this.state.showHelperText,
        streetAddress: false,
      },
      warningMessages: hasWarning && checked ? {} : { ...this.state.warningMessages },
      validationErrors: {}, // Reset the validationErrors for address if any.
    });
  };

  updateAddress = newAddress => {
    this.setState({
      address: newAddress,
      showHelperText: {
        ...this.state.showHelperText,
      },
    });
  };

  submit = e => {
    if (e) e.preventDefault();
    const secondaryUsePrimaryAddress = this.state.secondaryUsePrimaryAddress;
    const dataModel = this.state.address;
    const data =
      this.props.isSecondary && secondaryUsePrimaryAddress
        ? Object.assign(dataModel, { secondaryUsePrimaryAddress })
        : dataModel;
    const validationErrors = this.validateInput();
    const { warningMessages, hasSeenAndFixedPOWarning } = this.state;

    if (!Object.keys(validationErrors).length > 0) {
      // user submits page without fixing the P.O warning
      if (warningMessages.streetAddress) {
        heapTrackEvent('PL PI1 Address Page submitted with P.O Box warning', {
          eventValue: 'submitted page with P.O. warning',
        });
      } else if (hasSeenAndFixedPOWarning) {
        // user submits page without P.O warning (after fixing the warning at least once before)
        heapTrackEvent('PL PI1 Address Page submitted without P.O Box warning', {
          eventValue: 'submitted page without P.O. warning after fixing',
        });
      }
      this.props.saveValues({
        address: { ...data },
      });
      this.props.nextLocation();
    } else {
      this.setState({
        showHelperText: {
          ...this.state.showHelperText,
          [name]: name === 'streetAddress' && warningMessages.streetAddress && warningMessages.streetAddress.length > 0,
        },
        validationErrors,
      });
      heapTrackErrors(validationErrors);
    }
  };

  render() {
    const headerTitleProps = {
      primaryName: this.props.primaryName,
      secondaryName: this.props.secondaryName,
      secondaryUsePrimaryAddress: this.state.secondaryUsePrimaryAddress,
      streetAddress: this.props.address.streetAddress,
      isExperianDm: this.props.dm.isExperianDm,
      isSecondary: this.props.isSecondary,
      isSecondLoan: this.props.isSecondLoan,
      isDM: this.props.dm.isDm,
      hasPartialDMPII: this.props.dm.hasPartialDMPII,
    };

    const pageTitleHeader = getHeaderTitle(headerTitleProps);

    return (
      <Page
        title={pageTitleHeader.title}
        header={pageTitleHeader.header}
        name="address"
        goBack={this.props.previousLocation}
        submitHandler={this.submit}
      >
        <div className="form__input">
          <AutocompleteAddress
            address={this.state.address}
            autoFocus={!this.state.address.streetAddress}
            clearValidationErrors={this.clearValidationErrors}
            handleBlur={this.handleBlur}
            handleFocus={this.handleFocus}
            handleInputChange={this.handleInputChange}
            isSecondary={this.props.isSecondary}
            showHelperText={this.state.showHelperText}
            labels={{ street: 'Street Address' }}
            secondaryUsePrimaryAddress={this.state.secondaryUsePrimaryAddress}
            updateAddress={this.updateAddress}
            validationErrors={this.state.validationErrors}
            warningMessages={this.state.warningMessages}
          />
        </div>
        {this.props.isSecondary && (
          <Checkbox
            name="secondaryUsePrimaryAddress"
            checked={this.state.secondaryUsePrimaryAddress}
            onChange={this.handleCheckboxChange}
          >
            We share the same address
          </Checkbox>
        )}
      </Page>
    );
  }
}

Address.propTypes = {
  saveValues: func.isRequired,
  previousLocation: func.isRequired,
  nextLocation: func.isRequired,
  primaryName: shape({
    firstName: string,
    lastName: string,
  }),
  address: shape({
    streetAddress: string,
    city: string,
    state: string,
    zip: string,
    secondaryUsePrimaryAddress: bool,
  }),
  sharedAddress: shape({
    streetAddress: string,
    city: string,
    state: string,
    zip: string,
  }),
  secondaryName: string,
  isSecondary: bool.isRequired,
  isSecondLoan: bool,
  dm: shape({
    isDm: bool,
    isExperianDm: bool,
  }),
  addNewCoBorrower: bool,
  isRMEJA: bool,
};

Address.defaultProps = {
  primaryName: {
    firstName: '',
    lastName: '',
  },
  address: {
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    secondaryUsePrimaryAddress: false,
  },
  sharedAddress: {
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
  },
  secondaryName: '',
  isSecondLoan: false,
  dm: {
    isDm: false,
    isExperianDm: false,
  },
  addNewCoBorrower: false,
  isRMEJA: false,
};

export default Address;

require('/var/lib/jenkins/workspace/borrower-apply-ui_Release_0/src/pages/pi1-1QAAT/pages/Address/Address.scss');